import {
  AMBIGUITY_MARGIN_METERS,
  PkMeshVertex,
  PkFromMeshEntry,
  pkFromMesh,
  pkFromMeshEntries,
} from './pkFromMesh';
import {
  pkDirection,
  pkIsBetween,
  pkMinMax,
  sortByPk,
} from './pkCompare';
import { haversineMeters } from './geo';

/**
 * Por qué carreteras pasó un tramo (multi-carretera 2026-08-17).
 *
 * La patrulla se pasa de la troncal a su ramal sin cerrar el tramo — solo cierra
 * cuando pasa algo—, así que un tramo puede haber recorrido varias vías.
 *
 * Se descartó partir el tramo en el cruce, y el motivo es de dominio: **un tramo
 * termina por una CAUSA** (una pausa, una actuación, una llamada a sala).
 * Cerrarlo porque la vía cambió de nombre fabrica un evento que no ocurrió — una
 * fila que acaba sin motivo en la tabla y una parada que nadie hizo en el
 * informe. El operario no paró ahí.
 *
 * Así que el tramo se queda entero y registra sus trozos. Dos consecuencias que
 * conviene tener claras:
 *
 *  - **La distancia de un tramo es la SUMA de sus trozos**, no la resta de sus
 *    extremos. Los PK de dos carreteras no son comparables.
 *  - **No hace falta ninguna hora.** Partir exigía un `endDate` para el corte, y
 *    eso obligaba a tener rastro GPS o a inventárselo. Anotar solo necesita la
 *    frontera de PK, que se puede sacar de la geometría de las mallas — que
 *    están cacheadas y funcionan sin cobertura.
 */

/** Lo mínimo de un punto del rastro. Encaja con `LocalGpsPoint` tal cual. */
export type TrackPoint = {
  latitude: number;
  longitude: number;
  /** ISO. Hora del CLIENTE, capturada al vuelo. */
  timestamp: string;
};

export type RoadStretch = {
  roadId: string;
  pkInitKm: number;
  pkInitMeter: number;
  pkEndKm: number;
  pkEndMeter: number;
};

export type Pk = { km: number; meter: number };

/**
 * Puntos seguidos en una carretera nueva que hacen falta para dar el cambio por
 * bueno. No es para filtrar el asfalto compartido —de eso se encarga el margen
 * de ambigüedad de `pkFromMeshes`— sino para no morder un salto de GPS de los
 * que pasan bajo un puente. A ~4 s por punto, unos 12 s de marcha.
 */
export const MIN_CONSECUTIVE_POINTS = 3;


/**
 * Los trozos que recorrió el tramo, sacados del rastro ya grabado.
 *
 * Devuelve `[]` cuando todo el tramo fue por una sola carretera — que es el caso
 * normal y el que deja el tramo indistinguible de los de siempre. Devuelve
 * `null` cuando **no se puede afirmar nada**: sin puntos, sin mallas, o si el
 * rastro nunca pisó la carretera donde el tramo dice que empezó. `null` es «no
 * lo sé», y quien llama NO debe inventarse trozos con eso — para eso está el
 * respaldo del servidor, que deduce el cruce al cerrar.
 *
 * El primer trozo arranca en el `pkInit` **del tramo**, no en el del primer
 * punto GPS utilizable: el tramo pudo empezar antes de que hubiera un fix bueno.
 * El último acaba en el PK de cierre.
 */
export const buildRoadStretches = ({
  points,
  meshEntries,
  startRoadId,
  pkInit,
  pkEnd,
  minConsecutivePoints = MIN_CONSECUTIVE_POINTS,
}: {
  points: TrackPoint[];
  /**
   * Las mallas **por calzada** (2026-08-26). Antes se recibía una polilínea por
   * carretera —la canónica, elegida para PINTAR el mapa—, y proyectar sobre la
   * calzada equivocada tiene mediana 0-3 m pero cola larga: p95 de 84 m y
   * máximo de 309 en la C-17.
   */
  meshEntries: PkFromMeshEntry[];
  startRoadId: string;
  pkInit: Pk;
  pkEnd: Pk;
  minConsecutivePoints?: number;
}): RoadStretch[] | null => {
  if (!startRoadId) return null;
  if (new Set(meshEntries.map((e) => e.roadId)).size < 2) return [];
  if (points.length === 0) return null;

  // Un cambio se acepta cuando hay `minConsecutivePoints` seguidos en la vía
  // nueva. Hasta entonces la carretera «actual» no se mueve, y por eso la
  // preferencia de `pkFromMeshes` es SIEMPRE la actual: en el asfalto que la
  // troncal y su ramal comparten los candidatos están a metros, y ahí lo
  // correcto es no moverse de donde se venía.
  let currentRoadId = startRoadId;
  let lastOnCurrent: { pkKm: number; pkMeter: number } | null = null;
  let candidateRoadId: string | null = null;
  let candidateRun: { pkKm: number; pkMeter: number } | null = null;
  let consecutive = 0;
  let sawCurrent = false;

  const stretches: RoadStretch[] = [];
  let openPk: Pk = pkInit;

  for (const point of points) {
    const hit = pkFromMeshEntries(
      point.latitude,
      point.longitude,
      meshEntries,
      currentRoadId
    );
    // Fuera de toda malla (un área de servicio, un desvío) no dice nada: ni
    // confirma el cambio ni rompe la racha. Es ruido, no información.
    if (!hit) continue;

    if (hit.roadId === currentRoadId) {
      lastOnCurrent = { pkKm: hit.pkKm, pkMeter: hit.pkMeter };
      sawCurrent = true;
      candidateRoadId = null;
      candidateRun = null;
      consecutive = 0;
      continue;
    }

    if (hit.roadId === candidateRoadId) {
      consecutive += 1;
    } else {
      candidateRoadId = hit.roadId;
      // El PRIMER punto de la racha es donde se entra en la vía nueva: con él
      // se abre el trozo siguiente, y su PK va medido en SU carretera.
      candidateRun = { pkKm: hit.pkKm, pkMeter: hit.pkMeter };
      consecutive = 1;
    }

    if (consecutive >= minConsecutivePoints && candidateRun) {
      // Sin ningún punto en la carretera de partida no hay dónde cerrar el
      // trozo: el tramo entero transcurrió en otra vía, que es un problema
      // distinto (la carretera del tramo está mal desde que se abrió) y se
      // arregla corrigiéndolo, no troceándolo.
      if (!lastOnCurrent) return null;
      stretches.push({
        roadId: currentRoadId,
        pkInitKm: openPk.km,
        pkInitMeter: openPk.meter,
        pkEndKm: lastOnCurrent.pkKm,
        pkEndMeter: lastOnCurrent.pkMeter,
      });
      currentRoadId = candidateRoadId as string;
      openPk = { km: candidateRun.pkKm, meter: candidateRun.pkMeter };
      lastOnCurrent = null;
      candidateRoadId = null;
      candidateRun = null;
      consecutive = 0;
    }
  }

  if (!sawCurrent && stretches.length === 0) return null;
  if (stretches.length === 0) return [];

  stretches.push({
    roadId: currentRoadId,
    pkInitKm: openPk.km,
    pkInitMeter: openPk.meter,
    pkEndKm: pkEnd.km,
    pkEndMeter: pkEnd.meter,
  });
  return stretches;
};

/**
 * Los metros entre dos puntos de la MISMA carretera.
 *
 * 🔴 Restar `km * 1000 + meter` no vale, porque hay kilómetros largos: el 84 de
 * la C-17 mide 1.890 m, así que `85+000 − 84+1800` da **−800 m** cuando en el
 * asfalto son **+90**. Y no es un caso de laboratorio: la C-25 tiene 72
 * kilómetros largos de sus 153, y 22 de los 319 tramos de beta cruzan alguno.
 *
 * Con la malla se mide **el asfalto**, que es lo que el kilometraje quiere
 * decir: se suman las distancias entre los vértices que caen dentro, más los
 * dos trozos sueltos de los extremos. Sin malla se cae a la resta de siempre —
 * ni peor que antes ni bloqueado.
 */
const pkSpanMeters = (a: Pk, b: Pk, mesh?: PkMeshVertex[] | null): number => {
  const [lo, hi] = pkMinMax(a, b);
  // Dentro del mismo kilómetro los metros SÍ son comparables, y restar es
  // exacto: no hace falta malla ni interesa el ruido de medir sobre cuerdas.
  if (lo.km === hi.km) return hi.meter - lo.meter;

  const restaSimple = Math.abs(
    hi.km * 1000 + hi.meter - (lo.km * 1000 + lo.meter)
  );
  if (!mesh || mesh.length < 2) return restaSimple;

  const dentro = sortByPk(
    mesh
      .map((v) => ({
        km: v.pkKm,
        meter: v.pkMeter,
        latitude: v.latitude,
        longitude: v.longitude,
      }))
      .filter((v) => pkIsBetween(v, lo, hi))
  );
  // Sin al menos dos vértices dentro no hay nada que medir con criterio.
  if (dentro.length < 2) return restaSimple;

  let total = 0;
  for (let i = 1; i < dentro.length; i += 1) {
    total += haversineMeters(
      dentro[i - 1].latitude,
      dentro[i - 1].longitude,
      dentro[i].latitude,
      dentro[i].longitude
    );
  }
  // Los dos cabos: del extremo pedido al primer vértice, y del último al otro
  // extremo. Solo se pueden contar si caen en el MISMO kilómetro que su
  // vértice; si no, se dejan fuera antes que inventarlos.
  const primero = dentro[0];
  const ultimo = dentro[dentro.length - 1];
  if (primero.km === lo.km) total += primero.meter - lo.meter;
  if (ultimo.km === hi.km) total += hi.meter - ultimo.meter;
  return Math.max(0, Math.round(total));
};

/**
 * Los metros que recorrió un tramo.
 *
 * 🔴 Con varios trozos es la SUMA de lo hecho en cada vía, no la resta de los
 * extremos: los PK de dos carreteras no son comparables, y restarlos daría un
 * número que no significa nada.
 *
 * 🔴 Y dentro de cada vía tampoco se restan los PK a pelo: con un kilómetro
 * largo por medio la resta miente. Medido en beta, el tramo `82+800 → 237+100`
 * pintaba **154,3 km** donde el recorrido son **152,5**. Por eso acepta las
 * mallas: son las que saben cuánto mide de verdad cada kilómetro.
 *
 * Sin mallas se comporta como siempre — ni peor que antes ni bloqueado.
 */
export const segmentMeters = (
  segment: {
    roadId?: string | null;
    pkInitKm?: number | null;
    pkInitMeter?: number | null;
    pkEndKm?: number | null;
    pkEndMeter?: number | null;
    roadStretches?: RoadStretch[] | null;
  },
  meshesByRoadId?: Record<string, PkMeshVertex[]> | null
): number | null => {
  const stretches = segment.roadStretches;
  if (stretches && stretches.length > 0) {
    return stretches.reduce(
      (total, s) =>
        total +
        pkSpanMeters(
          { km: s.pkInitKm, meter: s.pkInitMeter },
          { km: s.pkEndKm, meter: s.pkEndMeter },
          meshesByRoadId?.[s.roadId]
        ),
      0
    );
  }
  if (
    segment.pkInitKm == null ||
    segment.pkInitMeter == null ||
    segment.pkEndKm == null ||
    segment.pkEndMeter == null
  ) {
    return null;
  }
  return pkSpanMeters(
    { km: segment.pkInitKm, meter: segment.pkInitMeter },
    { km: segment.pkEndKm, meter: segment.pkEndMeter },
    segment.roadId ? meshesByRoadId?.[segment.roadId] : undefined
  );
};

/** La carretera donde ACABÓ el tramo: la del último trozo, o la de partida. */
/**
 * Todas las carreteras que hay que nombrar en la pantalla de un recorrido: las
 * del recorrido MÁS las que aparezcan en sus tramos, sin repetir y con las del
 * recorrido primero.
 *
 * 🔴 No son el mismo conjunto, y ahí está el detalle: **quitar una carretera
 * del recorrido no reescribe el pasado**. Los tramos guardan por dónde pasaron
 * —es justo lo que se les pide— así que un tramo puede seguir apuntando a una
 * vía que el recorrido ya no cubre.
 *
 * Mirando solo el recorrido pasaban dos cosas, las dos silenciosas: la columna
 * «Carretera» de la tabla desaparecía (con una vía no hay nada que distinguir…
 * salvo que los tramos digan otra cosa) y el nombre de la vía que sobraba no
 * resolvía, así que la fila la dejaba fuera. Mientras tanto la ficha de cada
 * tramo seguía enseñando las dos: dos pantallas contándose cosas distintas
 * (2026-08-24, probando mc45).
 */
export const roadIdsInvolved = (
  routeRoadIds: string[] | null | undefined,
  segments: {
    roadId?: string | null;
    roadStretches?: { roadId: string }[] | null;
  }[]
): string[] => {
  const ids = new Set(routeRoadIds ?? []);
  for (const segment of segments) {
    (segment.roadStretches ?? []).forEach((st) => ids.add(st.roadId));
    if (segment.roadId) ids.add(segment.roadId);
  }
  return [...ids];
};

/**
 * La carretera por la que el tramo EMPEZÓ.
 *
 * Hermana de `endRoadIdOf`, y hace falta por lo mismo: un tramo que cruzó tiene
 * los dos extremos en vías distintas, así que el PK inicial se valida contra
 * esta y el final contra la otra. Sin cruce, las dos son la misma.
 */
export const startRoadIdOf = (segment: {
  roadId?: string | null;
  roadStretches?: { roadId: string }[] | null;
}): string | null | undefined => {
  const stretches = segment.roadStretches;
  if (stretches && stretches.length > 0) return stretches[0].roadId;
  return segment.roadId;
};

export const endRoadIdOf = (segment: {
  roadId?: string | null;
  roadStretches?: { roadId: string }[] | null;
}): string | null | undefined => {
  const stretches = segment.roadStretches;
  if (stretches && stretches.length > 0) {
    return stretches[stretches.length - 1].roadId;
  }
  return segment.roadId;
};

/**
 * Los puntos del rastro que caen dentro de la ventana de un tramo.
 *
 * El rastro se guarda por RECORRIDO, no por tramo, así que hay que acotarlo. El
 * extremo abierto (`endDate` ausente) es el caso normal aquí: se está cerrando
 * el tramo justo ahora.
 */
export const pointsWithin = (
  points: TrackPoint[],
  startDate: string | Date | null | undefined,
  endDate?: string | Date | null
): TrackPoint[] => {
  const from = startDate ? new Date(startDate).getTime() : NaN;
  const to = endDate ? new Date(endDate).getTime() : Infinity;
  if (isNaN(from)) return [];

  return points
    .filter((p) => {
      const at = new Date(p.timestamp).getTime();
      return !isNaN(at) && at >= from && at <= to;
    })
    .sort(
      (a, b) =>
        new Date(a.timestamp).getTime() - new Date(b.timestamp).getTime()
    );
};

/** ¿El PK cae dentro de los extremos declarados de una carretera? */
export const pkWithinRange = (
  pk: Pk,
  range?: { pkInitKm: number; pkInitMeter: number; pkEndKm: number; pkEndMeter: number } | null
): boolean => {
  if (!range) return false;
  // Los extremos pueden venir en cualquier orden: hay carreteras que se
  // recorren con el kilometraje decreciente. De eso se encarga `pkIsBetween`.
  //
  // 🔴 Antes comparaba en metros totales y eso **rechazaba PK legítimos**: en
  // una vía que acaba en `85+000`, un `84+1800` —válido, porque el kilómetro 84
  // de la C-17 mide 1.890 m— daba 85.800 contra 85.000 y se iba fuera. Es el
  // espejo exacto del mismo fallo en `pk-road-range.validator` del backend.
  return pkIsBetween(pk, 
    { km: range.pkInitKm, meter: range.pkInitMeter },
    { km: range.pkEndKm, meter: range.pkEndMeter }
  );
};

/**
 * Por qué carreteras pasó el tramo que se está cerrando.
 *
 * Vive aquí y no dentro del modal porque es la decisión, no la pantalla: así
 * se puede fijar con tests y no depende de un efecto de React.
 *
 * 🔴 **El rastro manda sobre el PK.** El orden importa y es la corrección del
 * 2026-08-19. Antes se miraba primero si el PK tecleado caía en la vía donde
 * empezó el tramo y, si caía, se daba por hecho que no hubo cruce SIN mirar el
 * rastro. Eso se tragaba el caso más normal del ramal: la C-25 y la C-25z
 * solapan sus rangos declarados dos kilómetros enteros (234+000 → 236+000), así
 * que una patrulla que se mete en el ramal y para en el 235+000 escribe un PK
 * que existe en las dos. El tramo quedaba como C-25 entera, con los kilómetros
 * contados sobre la troncal y pintado a 875 m de donde estuvo el coche —
 * teniendo el GPS la prueba grabada en el aparato.
 *
 * El rastro es prueba directa de por dónde se pasó; un PK del solape es
 * ambiguo por construcción. Ante los dos, gana el rastro.
 *
 * Devuelve `null` cuando no hay nada que registrar (ni cruce, ni pruebas), que
 * es lo que deja el tramo como mono-carretera de siempre.
 */
export const resolveClosingStretches = ({
  pk,
  pkInit,
  segmentRoadId,
  roadIds,
  rangesById,
  points,
  meshEntries,
}: {
  pk: Pk;
  pkInit: Pk;
  segmentRoadId?: string | null;
  roadIds: string[];
  rangesById: Record<string, { pkInitKm: number; pkInitMeter: number; pkEndKm: number; pkEndMeter: number } | undefined>;
  points: TrackPoint[];
  meshEntries: PkFromMeshEntry[];
}): RoadStretch[] | null => {
  if (!segmentRoadId || roadIds.length < 2) return null;

  // Puede no haber ninguna: en la franja compartida el PK vale en las dos y no
  // señala a ninguna. No es motivo para dejar de mirar el rastro.
  const otra = roadIds.find(
    (id) => id !== segmentRoadId && pkWithinRange(pk, rangesById[id])
  );

  // 1. El rastro, SIEMPRE.
  let stretches = buildRoadStretches({
    points,
    meshEntries,
    startRoadId: segmentRoadId,
    pkInit,
    pkEnd: pk,
  });

  // 2. Sin rastro utilizable ya NO se calcula nada aquí (2026-08-26).
  //
  // Antes había un respaldo geométrico que buscaba la bifurcación en las mallas
  // cacheadas. Se retira porque el cálculo pasó a vivir SOLO en el servidor: dos
  // implementaciones de la misma geometría acabaron dando números distintos
  // —19 m en el mejor caso, 200 m eligiendo mal la calzada— y el número que se
  // guardaba dependía de quién hubiera preguntado.
  //
  // El cierre no se queda sin nada: `finish()` deduce el cruce cuando el PK lo
  // delata, y lo hace ANTES de validar, así que el envío ya no se rechaza. Sin
  // cobertura, el reparto entre las dos vías llega al sincronizar; el tramo se
  // cierra igual, que es lo que importa.

  // 3. Los trozos tienen que explicar ESTE cierre: acaban donde acaba el tramo,
  //    así que el PK tecleado tiene que existir en la vía del último trozo.
  const acaba = stretches?.[stretches.length - 1]?.roadId;
  if (!stretches || stretches.length === 0 || !acaba) return null;
  return pkWithinRange(pk, rangesById[acaba]) ? stretches : null;
};
